import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { GRUPO_LABELS, formatDate, parseLocalDate } from "@/lib/constants";
import LoadError from "@/components/LoadError";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Cow, Drop, Calendar, Heartbeat, Baby, ChartLineUp, Egg, Percent } from "@phosphor-icons/react";

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#E2E0D8] rounded-sm p-5 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4E544B]">
          {label}
        </span>
        {Icon && <Icon size={20} weight="duotone" color="#2A5A3B" />}
      </div>
      <div className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-none break-words">
        {value}
      </div>
      {sub && <div className="text-xs text-[#4E544B]">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    Promise.all([api.get("/dashboard"), api.get("/dashboard/kpis")])
      .then(([r, k]) => {
        setData(r.data);
        setKpis(k.data);
      })
      .catch((e) =>
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message),
      );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <LoadError error={error} onRetry={load} />;
  if (!data) return <div className="text-[#4E544B]">Carregando painel…</div>;

  const grupoRows = Object.entries(data.grupos).map(([k, v]) => ({
    key: k,
    label: GRUPO_LABELS[k] || k,
    count: v,
  }));

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">Painel</h1>
        <p className="text-sm text-[#4E544B] mt-1">
          Visão geral do rebanho e da produção da propriedade.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          testid="stat-total"
          label="Animais ativos"
          value={data.total_ativos}
          sub="Rebanho total ativo"
          icon={Cow}
        />
        <Stat
          testid="stat-lactantes"
          label="Vacas em lactação"
          value={data.vacas_lactantes}
          sub={`DEL médio: ${data.del_medio_lactante} dias`}
          icon={Drop}
        />
        <Stat
          testid="stat-cios"
          label="Cios (21 dias)"
          value={data.cios_ultimos_21dias}
          sub="Ciclo reprodutivo recente"
          icon={Heartbeat}
        />
        <Stat
          testid="stat-hoje"
          label="Hoje"
          value={new Date().toLocaleDateString("pt-BR")}
          sub="Registros do dia"
          icon={Calendar}
        />
      </div>

      {kpis && (
        <div>
          <h2 className="font-heading font-bold text-xl tracking-tight mb-3">
            Indicadores zootécnicos
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat
              testid="kpi-iep"
              label="IEP (interv. entre partos)"
              value={kpis.iep_meses ? `${kpis.iep_meses}m` : "—"}
              sub={
                kpis.iep_dias
                  ? `${kpis.iep_dias} dias · ${kpis.iep_amostras} intervalos`
                  : "Aguarda 2+ partos por vaca"
              }
              icon={ChartLineUp}
            />
            <Stat
              testid="kpi-dpl"
              label="DPL (dias em aberto)"
              value={kpis.dpl_dias !== null ? `${kpis.dpl_dias}d` : "—"}
              sub={
                kpis.dpl_dias !== null
                  ? `${kpis.dpl_amostras} vacas com IA pós-parto`
                  : "Sem IA registrada após parto"
              }
              icon={Baby}
            />
            <Stat
              testid="kpi-taxa-concepcao"
              label="Taxa de concepção 12m"
              value={
                kpis.taxa_concepcao !== null ? `${kpis.taxa_concepcao}%` : "—"
              }
              sub={`${kpis.concepcoes_12m}/${kpis.inseminacoes_12m} inseminações`}
              icon={Percent}
            />
            <Stat
              testid="kpi-taxa-prenhez"
              label="Taxa de prenhez"
              value={kpis.taxa_prenhez !== null ? `${kpis.taxa_prenhez}%` : "—"}
              sub={`${kpis.prenhes} prenhes / ${kpis.aptas} aptas`}
              icon={Egg}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E2E0D8] rounded-sm p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading font-bold text-lg tracking-tight">
              Produção — últimos 7 dias
            </h3>
            <span className="text-xs uppercase tracking-wider text-[#4E544B]">
              litros
            </span>
          </div>
          <div className="h-64" data-testid="chart-producao">
            <ResponsiveContainer>
              <BarChart data={data.producao_7dias}>
                <CartesianGrid stroke="#E2E0D8" vertical={false} />
                <XAxis
                  dataKey="data"
                  tickFormatter={(d) =>
                    (parseLocalDate(d) || new Date(d)).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
                  }
                  stroke="#4E544B"
                  fontSize={11}
                />
                <YAxis stroke="#4E544B" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E2E0D8",
                    borderRadius: 2,
                  }}
                  labelFormatter={(d) => formatDate(d)}
                />
                <Bar dataKey="litros" fill="#2A5A3B" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E2E0D8] rounded-sm p-5">
          <h3 className="font-heading font-bold text-lg tracking-tight mb-4">
            Rebanho por grupo
          </h3>
          <div className="space-y-3">
            {grupoRows.map((g) => (
              <div key={g.key} className="flex items-center justify-between">
                <span className="text-sm">{g.label}</span>
                <span
                  data-testid={`group-count-${g.key}`}
                  className="text-sm font-bold bg-[#F6F5F2] border border-[#E2E0D8] px-3 py-1 rounded-sm min-w-[3rem] text-center"
                >
                  {g.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
